import { faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StatusCode } from "api/protocols";
import { DeleteLoyaltyProgramOffer, DeleteLoyaltyProgramReward, DeleteLoyaltyProgramTier, PostDuplicateLoyaltyProgramOffer, PostDuplicateLoyaltyProgramReward, PostDuplicateLoyaltyProgramTier, PutLoyaltyProgram } from "api/rpc/2024-04/facilityAdmin/customer/loyalty";
import Card from "components/card/Card";
import DataTable from "components/dataTable/DataTable";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import TextField from "components/form/textField/TextField";
import Toggle from "components/form/toggle/Toggle";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Popup from "components/popup/Popup";
import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import { displayCurrency, valueToString } from "helpers/Helpers";
import useModal from "hooks/modals/useModal";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React from "react";
import { useTranslation } from "react-i18next";
import { loadLoyaltyProgram, loadLoyaltyProgramOffers } from "redux/actions/facility";
import { showError, showSuccess } from "redux/actions/ui";
import { ILoyaltyProgram, IOffer, IReward, ITier } from "redux/reducers/models/loyalty";
import "../loyalty.scss";
import LoyaltyProgramOffersModal from "../pointsSteps/LoyaltyProgramOffersModal";
import LoyaltyProgramRewardsModal from "../pointsSteps/LoyaltyProgramRewardsModal";
import LoyaltyProgramTiersModal from "../tiersSteps/LoyaltyProgramTiersModal";
import SelectNew from "components/select/SelectNew";


interface IProps {
    offers: Array<IOffer>;
    rewards: Array<IReward>;
    tiers: Array<ITier & {rewards?: Array<IReward>}>;
    setOffers: React.Dispatch<React.SetStateAction<IOffer[]>>;
    setTiers: React.Dispatch<React.SetStateAction<(ITier & {rewards?: Array<IReward>})[]>>;
    loadPointsBased: (updatedProgram?: ILoyaltyProgram) => Promise<void>;
    loadTiersBased: () => Promise<void>;
    cashbackReward?: IReward;
    setCashbackReward?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export default function LoyaltyOverview(props: IProps) {
    const {offers, rewards, tiers, setOffers, setTiers, loadPointsBased, loadTiersBased, cashbackReward, setCashbackReward} = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const {facilityStore} = useAppSelector(store => store);
    const { state: offerModal, updateModal: updateOfferModal, closeModal: closeOfferModal } = useModal<{offer?: IOffer}>({offer: null});
    const { state: rewardModal, updateModal: updateRewardModal, closeModal: closeRewardModal } = useModal<{reward?: IReward}>({reward: null});
    const { state: tierModal, updateModal: updateTierModal, closeModal: closeTierModal } = useModal<{tier?: ITier}>({tier: null});
    const { state: deleteModal, updateModal: updateDeleteModal, closeModal: closeDeleteModal } = useModal<{offer?: IOffer; tier?: ITier; reward?: IReward}>({offer: null, tier: null, reward: null});
    const { state: duplicateModal, updateModal: updateDuplicateModal, closeModal: closeDuplicateModal } = useModal<{offer?: IOffer; tier?: ITier; reward?: IReward}>({offer: null, tier: null, reward: null});

    const { state: editModal, updateModal: updateEditModal, closeModal: closeEditModal } = useModal<{program?: ILoyaltyProgram}>({program: null});

    function getOfferTypeText(type: string, amount_spent: number) {
        switch (type){ 
            case "amount_spent":
                return `${displayCurrency(facilityStore?.facility?.default_currency_code, amount_spent)} ${t("spent")}`;
            case "product_purchased":
                return t("Product purchased");
            case "welcome_bonus":
                return t("Sign up");
            case "birthday_bonus": 
                return t("Birthday bonus");
            default:
                return t("");
        }
    }

    function getRewardTypeText(type: string, pointsRequired: number, discountValue: number) {
        switch (type){ 
            case "discount_amount":
                return `${pointsRequired} points = ${displayCurrency(facilityStore?.facility?.default_currency_code, discountValue)} off`;
            case "discount_percent":
                return `${pointsRequired} points = ${discountValue}% off`;
            case "free_product":
                return `${pointsRequired} points = Free Product`;
            case "birthday_bonus": 
                return t("Birthday bonus");
            default:
                return t("");
        }
    }

    function getDeleteModalText(type: "offer" | "reward" | "tier") {
        switch(type) {
            case "offer":
                return t("Delete Offer");
            case "reward": 
                return t("Delete Reward");
            case "tier":
                return t("Delete Tier");
            default: 
                return t("Delete");
        }
    }

    function getDuplicateModalText(type: "offer" | "reward" | "tier") {
        switch(type) {
            case "offer":
                return t("Duplicate Offer");
            case "reward": 
                return t("Duplicate Reward");
            case "tier":
                return t("Duplicate Tier");
            default: 
                return t("Duplicate");
        }
    }

    function getDeleteModalDescription(type: "offer" | "reward" | "tier") {
        switch(type) {
            case "offer":
                return `${t("Are you sure you want to delete offer")}: ${deleteModal?.offer?.title}?`;
            case "reward": 
                return `${t("Are you sure you want to delete reward")}: ${deleteModal?.reward?.name}?`;
            case "tier":
                return `${t("Are you sure you want to delete tier")}: ${deleteModal?.tier?.title}?`;
            default: 
                return t("Are you sure you want to delete?");
        }
    }

    function getDuplicateModalDescription(type: "offer" | "reward" | "tier") {
        switch(type) {
            case "offer":
                return `${t("Are you sure you want to duplicate offer")}: ${duplicateModal?.offer?.title}?`;
            case "reward": 
                return `${t("Are you sure you want to duplicate reward")}: ${duplicateModal?.reward?.name}?`;
            case "tier":
                return `${t("Are you sure you want to duplicate tier")}: ${duplicateModal?.tier?.title}?`;
            default: 
                return t("Are you sure you want to duplicate?");
        }
    }

    function handleToggleStatus(index: number, type: "offers" | "rewards" | "tiers") {
        if (type === "offers" && offers) {
            const updatedOffers = [...offers]?.map((offer, offerIndex) => offerIndex === index ? {...offer, active: !offer.active} : offer);
            setOffers(updatedOffers);
        } else if (type === "tiers" && tiers) {
            const updatedTiers = [...tiers]?.map((tier, tierIndex) => tierIndex === index ? {...tier, active: tier?.active === 0 ? 1 : 0} : tier);
            setTiers(updatedTiers);
        }
    }

    async function handleAddOffer(offer: Partial<IOffer>) {
        if (!offer?.title) {
            dispatch(showError(t("Title required")));
            return;
        }

        if (offer?.points_expiry_type === "expire_after_days" && isNaN(offer?.points_expiry_days)) {
            dispatch(showError(t("Expiry days required")));
            return;
        }
        
        const res = await PutLoyaltyProgram({offers: [offer]}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(t("Error updating offers")));
            return;
        }
        dispatch(showSuccess(t("Successfully added offer")));

        // Sync store immediately so register uses the updated offer rates
        const updatedOffers = offer?.id
            ? offers.map(existing => (existing.id === offer.id ? ({ ...existing, ...offer } as IOffer) : existing))
            : [...offers, offer as IOffer];
        await dispatch(loadLoyaltyProgramOffers(updatedOffers.map(existing => ({ ...existing }))));

        //refresh offers
        await loadPointsBased();
        void closeOfferModal();
    }

    async function handleAddReward(reward: Partial<IReward>) {
        if (!reward?.name) {
            dispatch(showError(t("Name required")));
            return;
        }
        
        const res = await PutLoyaltyProgram({rewards: [reward]}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(t("Error updating reward")));
            return;
        }
        dispatch(showSuccess(t("Successfully added reward")));
        //refresh rewards
        await loadPointsBased();
        void closeRewardModal();
    }

    async function handleAddTier(tier: Partial<ITier>) {        
        const res = await PutLoyaltyProgram({tiers: [tier]}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(t("Error updating tier")));
            return;
        }
        dispatch(showSuccess(t("Successfully updated tier")));
        //refresh tiers
        await loadTiersBased();
        void closeTierModal();
    }

    async function handleDelete(type: "offer" | "reward" | "tier") {
        if (type === "offer") {
            const res = await DeleteLoyaltyProgramOffer({id: deleteModal?.offer?.id}, true);
            if (res?.status !== StatusCode.OK) {
                dispatch(showError(t("Error deleting offer")));
                return;
            }
            dispatch(showSuccess(t("Successfully deleted offer")));
        } else if (type === "reward") {
            const res = await DeleteLoyaltyProgramReward({id: deleteModal?.reward?.id}, true);
            if (res?.status !== StatusCode.OK) {
                dispatch(showError(t("Error deleting reward")));
                return;
            }
            dispatch(showSuccess(t("Successfully deleted reward")));
        } else if (type === "tier") {
            const res = await DeleteLoyaltyProgramTier({id: deleteModal?.tier?.id}, true);
            if (res?.status !== StatusCode.OK) {
                dispatch(showError(t("Error deleting tier")));
                return;
            }
            dispatch(showSuccess(t("Successfully deleted tier")));
        } else {
            return;
        }

        //Refresh data
        if (facilityStore?.loyaltyProgram?.type === "points_based" || facilityStore?.loyaltyProgram?.type === "cash_back") {
            await loadPointsBased();
        } else {
            await loadTiersBased();
        }
        void closeDeleteModal();
    }

    async function handleDuplicate(type: "offer" | "reward" | "tier") {
        if (type === "offer") {
            const res = await PostDuplicateLoyaltyProgramOffer({id: duplicateModal?.offer?.id}, true);
            if (res?.status !== StatusCode.OK) {
                dispatch(showError(t("Error duplicating offer")));
                return;
            }
            dispatch(showSuccess(t("Successfully duplicated offer")));
        } else if (type === "reward") {
            const res = await PostDuplicateLoyaltyProgramReward({id: duplicateModal?.reward?.id}, true);
            if (res?.status !== StatusCode.OK) {
                dispatch(showError(t("Error duplicating reward")));
                return;
            }
            dispatch(showSuccess(t("Successfully duplicated reward")));
        } else if (type === "tier") {
            const res = await PostDuplicateLoyaltyProgramTier({id: duplicateModal?.tier?.id}, true);
            if (res?.status !== StatusCode.OK) {
                dispatch(showError(t("Error duplicating tier")));
                return;
            }
            dispatch(showSuccess(t("Successfully duplicated tier")));
        } else {
            return;
        }

        //Refresh data
        if (facilityStore?.loyaltyProgram?.type === "points_based" || facilityStore?.loyaltyProgram?.type === "cash_back") {
            await loadPointsBased();
        } else {
            await loadTiersBased();
        }
        void closeDuplicateModal();
    }

    async function updateLoyaltyProgram() {
        if (!editModal?.program?.name) {
            dispatch(showError(t("Program name required")));
            return;
        }
        const res = await PutLoyaltyProgram({...editModal?.program}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(t("Error updating loyalty program")));
            return;
        }

        if (res?.data?.type === "cash_back" || res?.data?.type === "points_based") {
            await loadPointsBased(res?.data as ILoyaltyProgram);
        } else {
            await loadTiersBased();
        }

        dispatch(showSuccess(t("Successfully updated loyalty program")));
        void closeEditModal();
        if (res?.data) {
          void dispatch(loadLoyaltyProgram(res?.data as ILoyaltyProgram));
        }
    }

    return (
        <>
                    <Card title={t("Program Overview")} titleActions={[{content: "Edit", buttonType: "secondary", action: () => updateEditModal({program: {...facilityStore?.loyaltyProgram}, isOpen: true}), disabled: !facilityStore?.loyaltyProgram}]}>
                        <Card.Section>
                            <FormLayout>
                                <FormLayout.Group>
                                    <div className="loyalty-overview-text-group">
                                        <p className="loyalty-overview-label">{t("Program name")}</p>
                                        <p className="loyalty-overview-primary">{facilityStore?.loyaltyProgram?.name}</p>
                                    </div>
                                    <div className="loyalty-overview-text-group">
                                        <p className="loyalty-overview-label">{t("Program type")}</p>
                                        <p className="loyalty-overview-primary">{valueToString(facilityStore?.loyaltyProgram?.type)}</p>
                                    </div>
                                </FormLayout.Group>
                                <FormLayout.Group>
                                <div className="loyalty-overview-text-group">
                                        <p className="loyalty-overview-label">{t("Customer description")}</p>
                                        <p className="loyalty-overview-primary">{facilityStore?.loyaltyProgram?.welcome_text}</p>
                                    </div>
                                </FormLayout.Group>
                            </FormLayout>
                        </Card.Section>
                    </Card>
                    {facilityStore?.loyaltyProgram?.type === "points_based" ? (
                        //POINTS BASED
                        <>
                        <Card title={t("Points")} subtitle={t("Set how customers earn points for purchases")} 
                        // titleActions={[{content: "+ Add Ways to Earn", buttonType: "secondary", action: () => updateOfferModal({isOpen: true})}]}
                        >
                <Card.Section>
                <br />
                <div className="loyalty-data-table">
                <DataTable
                    columns={[{ label: t("How to earn") }, { label: "", width: "40%" }, { label: t("Status") }, { label: "" }]}
                    className="min-h-auto"
                >
                    {offers?.map((offer, index) => {
                        return (
                            <tr key={index}>
                                <td>
                                    <p style={{color: "#101828"}}>{offer?.title}</p>
                                    <p>{getOfferTypeText(offer?.type, offer?.amount_spent)} = {offer?.points_earned} {t("Points")}</p>
                                </td>
                                <td></td>
                                <td>
                                    <Toggle checked={!!offer?.active} onChange={() => handleToggleStatus(index, "offers")}/>
                                </td>

                                <td>
                                <NavigationDropdownNew
                            showPlainTextLabel
                            rightAlign
                            label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                            sections={[
                                [
                                {
                                    label: t("Edit"),
                                    icon: "pen-to-square",
                                    onClick: () => updateOfferModal({isOpen: true, offer: offer}),
                                },
                                // {
                                //     label: t("Duplicate"),
                                //     icon: "copy",
                                //     onClick: () => updateDuplicateModal({isOpen: true, offer: offer}),
                                // },
                                // {
                                //     label: t("Delete"),
                                //     icon: "trash-can",
                                //     onClick: () => updateDeleteModal({isOpen: true, offer: offer}),
                                // },
                                ],
                            ]}
                            />
                                </td>
                            </tr>
                        );
                    })}
                </DataTable>
                </div>
                </Card.Section>
            </Card>
            <Card title={t("Rewards")} subtitle={t("Choose what customers can redeem their points for")} titleActions={[{content: "+ Create Reward", buttonType: "secondary", action: () => updateRewardModal({isOpen: true})}]}>
                <Card.Section>
                <br />
                <div className="loyalty-data-table">
                <DataTable
                    columns={[{ label: t("How to redeem points") }, { label: "", width: "50%" }, { label: "" }]}
                    className="min-h-auto"
                >
                    {rewards?.filter(reward => facilityStore?.loyaltyProgram?.type === "points_based" ? !reward?.loyalty_program_tier_id : reward?.loyalty_program_tier_id)?.map((reward, index) => {
                        return (
                            <tr key={index}>
                                <td>
                                    <p style={{color: "#101828"}}>{reward?.name}</p>
                                    <p>{getRewardTypeText(reward?.reward_type, reward?.points_required, reward?.discount_value)}</p>
                                </td>
                                <td></td>

                                <td>
                                <NavigationDropdownNew
                            showPlainTextLabel
                            rightAlign
                            label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                            sections={[
                                [
                                {
                                    label: t("Edit"),
                                    icon: "pen-to-square",
                                    onClick: () => updateRewardModal({isOpen: true, reward: reward}),
                                },
                                {
                                    label: t("Duplicate"),
                                    icon: "copy",
                                    onClick: () => updateDuplicateModal({isOpen: true, reward: reward}),
                                },
                                {
                                    label: t("Delete"),
                                    icon: "trash-can",
                                    onClick: () => updateDeleteModal({isOpen: true, reward: reward}),
                                },
                                ],
                            ]}
                            />
                                </td>
                            </tr>
                        );
                    })}
                </DataTable>
                </div>
                </Card.Section>
            </Card>
                        </>
                    ) : facilityStore?.loyaltyProgram?.type === "cash_back" ? (
                        <>
                        <Card title={t("Points")} subtitle={t("Set how customers earn points for purchases")} 
                        // titleActions={[{content: "+ Add Ways to Earn", buttonType: "secondary", action: () => updateOfferModal({isOpen: true})}]}
                        >
                        <Card.Section>
                        <br />
                        <div className="loyalty-data-table">
                        <DataTable
                            columns={[{ label: t("How to earn") }, { label: "", width: "40%" }, { label: t("Status") }, { label: "" }]}
                            className="min-h-auto"
                        >
                            {offers?.map((offer, index) => {
                                return (
                                    <tr key={index}>
                                        <td>
                                            <p style={{color: "#101828"}}>{offer?.title}</p>
                                            <p>{getOfferTypeText(offer?.type, offer?.amount_spent)} = {offer?.points_earned} {t("Points")}</p>
                                        </td>
                                        <td></td>
                                        <td>
                                            <Toggle checked={!!offer?.active} onChange={() => handleToggleStatus(index, "offers")}/>
                                        </td>

                                        <td>
                                        <NavigationDropdownNew
                                    showPlainTextLabel
                                    rightAlign
                                    label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                                    sections={[
                                        [
                                        {
                                            label: t("Edit"),
                                            icon: "pen-to-square",
                                            onClick: () => updateOfferModal({isOpen: true, offer: offer}),
                                        },
                                        // {
                                        //     label: t("Duplicate"),
                                        //     icon: "copy",
                                        //     onClick: () => updateDuplicateModal({isOpen: true, offer: offer}),
                                        // },
                                        // {
                                        //     label: t("Delete"),
                                        //     icon: "trash-can",
                                        //     onClick: () => updateDeleteModal({isOpen: true, offer: offer}),
                                        // },
                                        ],
                                    ]}
                                    />
                                        </td>
                                    </tr>
                                );
                            })}
                        </DataTable>
                        </div>
                        </Card.Section>
                    </Card>
                        <Card title={t("Cash back reward")} subtitle={t("Set how many points are required for each dollar of cash back.")}>
                            <Card.Section>
                                <FormLayout>
                                    <FormLayout.Group>
                                        <Input label={t("Reward name")} value={cashbackReward?.name} id="name" onChange={setCashbackReward} error={!cashbackReward?.name}/>
                                    </FormLayout.Group>
                                    <FormLayout.Group>
                                        <Input label={t("Points")} value={cashbackReward?.cash_back_points} suffix={t("Pts")} id="cash_back_points" type="number" onChange={setCashbackReward} error={!cashbackReward?.cash_back_points}/>
                                        <div className="loyalty-equals-container">
                                            <div className="loyalty-equals">=</div>
                                        </div>
                                        <Input className="w-100" label={t("Value")} value={cashbackReward?.cash_back_value} prefix={facilityStore?.facility?.default_currency_symbol} id="cash_back_value" type="number" onChange={setCashbackReward} error={!cashbackReward?.cash_back_value}/>
                                    </FormLayout.Group>
                                </FormLayout>
                            </Card.Section>
                        </Card>
                        </>
                    )
                    : (
                        //TIER BASED
                        <>
                <Card title={t("Tiers")} subtitle={t("Manage tiers and its rewards to motivate your customers")} titleActions={[{content: "+ Add Tier", buttonType: "secondary", action: () => updateTierModal({isOpen: true})}]}>
                <Card.Section>
                    <br />
                    <div className="loyalty-data-table">
                    <DataTable
                    columns={[{ label: t("How to Redeem Points") }, { label: "", width: "40%" }, { label: t("Rewards") }, { label: t("Status") }, { label: "" }]}
                    className="min-h-auto"
                >
                    {tiers?.map((tier, index) => {
                    return (
                        <tr key={index}>
                            <td>
                                <p style={{color: "#101828"}}>{tier?.title}</p>
                                <p>{tier?.points_required} {t("Points Required")}</p>
                            </td>
                            <td></td>
                            <td>{tier?.rewards?.length}</td>
                            <td>
                                <Toggle checked={!!tier?.active} onChange={() => handleToggleStatus(index, "tiers")}/>
                            </td>

                            <td>
                            <NavigationDropdownNew
                          showPlainTextLabel
                          rightAlign
                          label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                          sections={[
                            [
                              {
                                label: t("Edit"),
                                icon: "pen-to-square",
                                onClick: () => updateTierModal({isOpen: true, tier: tier}),
                              },
                              {
                                label: t("Duplicate"),
                                icon: "copy",
                                onClick: () => updateDuplicateModal({isOpen: true, tier: tier}),
                              },
                              {
                                label: t("Delete"),
                                icon: "trash-can",
                                onClick: () => updateDeleteModal({isOpen: true, tier: tier}),
                              },
                            ],
                          ]}
                        />
                            </td>
                        </tr>
                    );
                })}
                    </DataTable>
                    </div>
                </Card.Section>
            </Card>
                        </>
                    )}


            <LoyaltyProgramOffersModal open={offerModal?.isOpen} onCancel={() => closeOfferModal()} onOk={offer => handleAddOffer(offer)} offer={offerModal?.offer}/>
            <LoyaltyProgramRewardsModal open={rewardModal?.isOpen} onCancel={closeRewardModal} onOk={reward => handleAddReward(reward)} reward={rewardModal?.reward}/>
            <LoyaltyProgramTiersModal open={tierModal?.isOpen} onCancel={closeTierModal} onOk={tier => handleAddTier(tier)} tier={tierModal?.tier} />

            <Popup 
            title={getDeleteModalText(deleteModal?.offer ? "offer" : deleteModal?.reward ? "reward" : "tier")} 
            type="warning" 
            description={getDeleteModalDescription(deleteModal?.offer ? "offer" : deleteModal?.reward ? "reward" : "tier")} 
            open={deleteModal?.isOpen} 
            onCancel={closeDeleteModal} 
            onOk={() => handleDelete(deleteModal?.offer ? "offer" : deleteModal?.reward ? "reward" : "tier")} 
            okText={t("Delete")} />

            <Popup 
                title={getDuplicateModalText(duplicateModal?.offer ? "offer" : duplicateModal?.reward ? "reward" : "tier")} 
                type="info" 
                description={getDuplicateModalDescription(duplicateModal?.offer ? "offer" : duplicateModal?.reward ? "reward" : "tier")} 
                open={duplicateModal?.isOpen} 
                onCancel={closeDuplicateModal} 
                onOk={() => handleDuplicate(duplicateModal?.offer ? "offer" : duplicateModal?.reward ? "reward" : "tier")} 
                okText={t("Duplicate")} />

                 <Portal isMounted={editModal?.isOpen}>
                    <Sheet
                    title={t("Program Details")}
                    supportText={t("Add key details customers will see about this program")}
                    size="small"
                    open={editModal?.isOpen}
                    onCancel={closeEditModal}
                    onOk={() => updateLoyaltyProgram()}
                    okText={t("Save")}
                    >
                        <FormLayout>
                            <FormLayout.Group>
                                <Input 
                                label={t("Program Name")} 
                                maxLength={100} 
                                value={editModal?.program?.name} 
                                placeholder={t("Program Name")} 
                                onChange={e => updateEditModal({program: {...editModal?.program, name: e.target.value}})} />
                            </FormLayout.Group>
                            <FormLayout.Group>
                                <TextField 
                                label={(t("Customer-Facing Description"))}
                                value={editModal?.program?.welcome_text}
                                maxLength={300}
                                rows={4}
                                placeholder={t("Add a short description customers will see. Ex: Unlock free rounds and member-only perks.")}
                                characterCountBottom
                                onChange={e => updateEditModal({program: {...editModal?.program, welcome_text: e.target.value}})}
                                 />
                            </FormLayout.Group>
                            <FormLayout.Group>
                            <SelectNew
                                id={"program_type"}
                                label={t("Program Type")}
                                placeholder={t("Select Program Type")}
                                onChange={(type: "points_based" | "tier_based" | "cash_back") => updateEditModal({program: {...editModal?.program, type}})}
                                value={editModal?.program?.type}
                                options={[
                                    { label: t("Points-based"), id: "points_based" },
                                    { label: t("Tier-based"), id: "tier_based" },
                                    { label: t("Cash back"), id: "cash_back" },
                                ]}
                                usePortal
                                tooltip={<div>
                                    <p className="font-semibold">{t("Tier-based program:")}</p> <p>{t("Set levels for customers to hit each tier to unlock better perks or earn rates.")}</p>
                                     <br/> 
                                     <p className="font-semibold">{t("Points-based program:")}</p> <p>{t("Earn points for spending or actions and redeem them for rewards.")}</p>
                                     <br/> 
                                     <p className="font-semibold">{t("Cash back program:")}</p> <p>{t("Redeem points for cash back discounts on orders.")}</p>
                                     </div>}
                            />
                            </FormLayout.Group>
                        </FormLayout>
                    </Sheet>
                </Portal>
            
                    </>
    );
}
